import { useEffect, useRef, useState, useCallback } from 'react';
import { X, MapPin, Search, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons broken by Vite/webpack asset hashing
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Default center: India
const INDIA_CENTER = [20.5937, 78.9629];

/**
 * Full-screen OpenStreetMap location picker.
 *
 * Props:
 *   initialCoords  – { latitude, longitude } — optional starting position
 *   onConfirm      – ({ latitude, longitude }) => void
 *   onCancel       – () => void
 */
const MapLocationPicker = ({ initialCoords, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const searchBarRef = useRef(null);
  const debounceRef = useRef(null);

  const resolvedCoords = initialCoords ?? (() => {
    try {
      const saved = localStorage.getItem('lastSearchLocation');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  })();

  const startCenter = resolvedCoords
    ? [resolvedCoords.latitude, resolvedCoords.longitude]
    : INDIA_CENTER;
  const startZoom = resolvedCoords ? 15 : 5;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, { zoomControl: true }).setView(startCenter, startZoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker(startCenter, { draggable: true }).addTo(map);

    // Clicking the map moves the marker to the tapped position
    map.on('click', (e) => {
      marker.setLatLng(e.latlng);
    });

    mapRef.current = map;
    markerRef.current = marker;

    // Ensure Leaflet measures the correct container size after CSS layout settles
    setTimeout(() => map.invalidateSize(), 100);

    // Auto-zoom to current GPS if permission is already granted (no prompt)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          marker.setLatLng([latitude, longitude]);
          map.setView([latitude, longitude], 16, { animate: true });
        },
        () => { /* permission not granted or location off — silent, stay at last known */ },
        { timeout: 5000, maximumAge: 30000 },
      );
    }

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    if (!markerRef.current) return;
    const { lat, lng } = markerRef.current.getLatLng();
    try { localStorage.setItem('lastSearchLocation', JSON.stringify({ latitude: lat, longitude: lng })); } catch { /* quota exceeded */ }
    onConfirm({ latitude: lat, longitude: lng });
  };

  // ── OLA Maps Places API (falls back to Photon if no API key configured) ─────
  const OLA_KEY = import.meta.env.VITE_OLA_MAPS_API_KEY;
  const useOla = !!OLA_KEY;

  const getBiasCoords = useCallback(() => {
    if (mapRef.current) {
      const c = mapRef.current.getCenter();
      return { lat: c.lat, lon: c.lng };
    }
    if (resolvedCoords) return { lat: resolvedCoords.latitude, lon: resolvedCoords.longitude };
    return { lat: INDIA_CENTER[0], lon: INDIA_CENTER[1] }; // generic India center
  }, [initialCoords]);

  const searchPlaces = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); setShowResults(false); setNoResults(false); return; }
    setIsSearching(true);
    setNoResults(false);
    if (searchBarRef.current) {
      setDropdownTop(searchBarRef.current.getBoundingClientRect().bottom);
    }
    try {
      let features = [];
      const { lat, lon } = getBiasCoords();

      if (useOla) {
        // OLA Maps autocomplete — best India coverage
        const url = `https://api.olamaps.io/places/v1/autocomplete?input=${encodeURIComponent(q)}&location=${lat},${lon}&api_key=${OLA_KEY}`;
        const res = await fetch(url);
        const data = await res.json();
        features = data?.predictions ?? [];
      } else {
        // Fallback: Photon
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&lang=en&lat=${lat}&lon=${lon}&location_bias_scale=0.9&bbox=68.1,8.0,97.4,37.1`;
        const res = await fetch(url);
        const data = await res.json();
        features = data?.features ?? [];
      }

      if (features.length === 0) {
        setNoResults(true);
        setSearchResults([]);
        setShowResults(false);
      } else {
        setSearchResults(features);
        setShowResults(true);
        setNoResults(false);
      }
    } catch {
      setSearchResults([]);
      setNoResults(true);
    } finally {
      setIsSearching(false);
    }
  }, [getBiasCoords, useOla, OLA_KEY]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setNoResults(false);
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setSearchResults([]); setShowResults(false); return; }
    debounceRef.current = setTimeout(() => searchPlaces(val), 400);
  };

  // OLA Maps: need a second call to /places/v1/details to resolve lat/lng from place_id
  const handleResultSelect = async (result) => {
    setShowResults(false);
    setSearchResults([]);

    if (useOla) {
      // OLA prediction has description but no coords — fetch details
      setSearchQuery(result.description ?? result.structured_formatting?.main_text ?? '');
      try {
        const detailUrl = `https://api.olamaps.io/places/v1/details?place_id=${result.place_id}&api_key=${OLA_KEY}`;
        const res = await fetch(detailUrl);
        const data = await res.json();
        const loc = data?.result?.geometry?.location;
        if (loc && mapRef.current && markerRef.current) {
          markerRef.current.setLatLng([loc.lat, loc.lng]);
          mapRef.current.setView([loc.lat, loc.lng], 16, { animate: true, duration: 0.5 });
        }
      } catch { /* pin stays where it is */ }
    } else {
      // Photon: coords are in GeoJSON geometry
      const [lng, lat] = result.geometry.coordinates;
      setSearchQuery(result.properties?.name || '');
      if (mapRef.current && markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
        mapRef.current.setView([lat, lng], 16, { animate: true, duration: 0.5 });
      }
    }
  };

  // Label helper (works for both OLA and Photon results)
  const getResultLabel = (r) => {
    if (useOla) return r.description ?? r.structured_formatting?.main_text ?? '';
    const p = r.properties;
    return [p.name, p.locality, p.city, p.state, p.country].filter(Boolean).join(', ');
  };

  const getResultKey = (r, i) => useOla ? (r.place_id ?? i) : (r.properties?.osm_id ?? i);

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-white dark:bg-background-200">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 shrink-0 bg-white dark:bg-background-200 shadow-sm z-10">
        <button
          onClick={onCancel}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-border-100 transition-colors"
          aria-label="Close map"
        >
          <X className="w-5 h-5 text-gray-600 dark:text-foreground-200" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-gray-900 dark:text-foreground-100 text-base">
            {t('locationGateModal.mapPickerTitle', 'Pin your delivery location')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-foreground-300 mt-0.5">
            {t('locationGateModal.mapPickerHint', 'Drag or tap anywhere to set your location')}
          </p>
        </div>
      </div>

      {/* Search bar */}
      <div ref={searchBarRef} className="px-4 py-2 shrink-0 bg-white dark:bg-background-200 border-b border-gray-100 dark:border-border-200">
        <div className="relative flex items-center">
          <Search className="absolute left-3 w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => {
              if (searchBarRef.current) {
                setDropdownTop(searchBarRef.current.getBoundingClientRect().bottom);
              }
              if (searchResults.length > 0) setShowResults(true);
            }}
            placeholder={t('locationGateModal.searchPlaceholder', 'Search for an area or landmark')}
            className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-border-200 bg-gray-50 dark:bg-background-100 text-sm text-gray-800 dark:text-foreground-100 placeholder-gray-400 dark:placeholder-foreground-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 w-4 h-4 text-gray-400 animate-spin" />
          )}
          {!isSearching && searchQuery && (
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                setSearchQuery('');
                setSearchResults([]);
                setShowResults(false);
                setNoResults(false);
              }}
              className="absolute right-3 p-0.5"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
        </div>
        {/* No results hint */}
        {noResults && searchQuery && !isSearching && (
          <p className="mt-1.5 text-xs text-gray-400 dark:text-foreground-300 px-1">
            No places found — try a city, area, or landmark name
          </p>
        )}
      </div>

      {/* Dropdown — fixed-positioned so it's above the Leaflet map */}
      {showResults && searchResults.length > 0 && (
        <div
          style={{ top: dropdownTop }}
          className="fixed left-4 right-4 bg-white dark:bg-background-200 rounded-xl shadow-2xl border border-gray-100 dark:border-border-200 overflow-hidden max-h-64 overflow-y-auto z-[9999]"
        >
          {searchResults.map((r, i) => (
            <button
              key={getResultKey(r, i)}
              onPointerDown={(e) => { e.preventDefault(); handleResultSelect(r); }}
              className="w-full flex items-start gap-2 px-4 py-3 hover:bg-gray-50 dark:hover:bg-border-100 active:bg-gray-100 transition-colors text-left border-b border-gray-100 dark:border-border-200 last:border-0"
            >
              <MapPin className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700 dark:text-foreground-200 leading-snug line-clamp-2">
                {getResultLabel(r)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Map fills all remaining space */}
      <div ref={containerRef} className="flex-1" onClick={() => { setShowResults(false); setNoResults(false); }} />

      {/* Confirm button */}
      <div className="px-4 py-4 shrink-0 bg-white dark:bg-background-200 shadow-[0_-4px_16px_rgba(0,0,0,0.10)]">
        <button
          onClick={handleConfirm}
          className="w-full py-3.5 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          <MapPin className="w-5 h-5" />
          {t('locationGateModal.confirmLocation', 'Confirm this location')}
        </button>
      </div>
    </div>
  );
};

export default MapLocationPicker;
